// İSG Portal — Tag (S33 planı §5.1/§1, Slice 1b): Carbon'un durum rozeti. Uygulamanın
// bugünkü rozeti (`apps/web-expert/src/components/contractors/Badge.tsx`, 26 tüketici) YERİNE
// GEÇMEZ — o dosyadaki ton türetme fonksiyonları (§13 iş kuralı) korunur, yalnız RENK
// paletini bu primitife taşımaları Slice 4'ün işi. Bu bileşen henüz HİÇBİR sayfada
// kullanılmıyor (ilk tüketici Slice 3/4) — Storybook'un dışında bu dilimde import eden yok.
//
// Renk sözlüğü tam olarak plan §5.1'in "ayarlanmış" eşlemesi: yalnız 4 renk (mavi/kırmızı/
// yeşil/gri) — turuncu/sarı YOK (Carbon'da sarı metin/dolgu olarak okunmaz). `Pending` → gri,
// `Suspended` → kırmızı, `Expiring` → gri + `icon`, `Missing` → kırmızı, `accent`/`Yetkili` →
// mavi (eski rozetin turuncu "accent" tonunun göç hedefi — turuncu Carbon'da yalnız logoda
// kalır). Ton→renk EŞLEMESİ bu dosyanın işi DEĞİL — çağıran ekran (Slice 3/4) kendi §13
// mantığını `color` prop'una çevirir, tıpkı bugün `Badge`'in `tone` prop'una çevirdiği gibi.
//
// 24px hap şekli (`rounded-full`, `h-6`) HER İKİ derlemede AYNI — Carbon'un "zero radius"
// süpürmesinin TEK istisnası (plan §2.2, Tag/avatar). Yalnız RENK carbon: ile değişir: base
// (carbon dışı) derlemede en yakın var olan token'lara düşer (bu component'in henüz gerçek bir
// tüketicisi yokken "kırık" görünmemesi için), Carbon derlemesinde plan §5.1/Stage 1'in tam
// `info-subtle/info` · `danger-subtle/danger-strong` · `success-subtle/success` ·
// `surface-muted/fg` eşlemesine geçer.

import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from './cn';

export type TagColor = 'blue' | 'red' | 'green' | 'gray';

const colors: Record<TagColor, string> = {
  blue: 'bg-primary/10 text-primary carbon:bg-info-subtle carbon:text-info',
  red: 'bg-danger-subtle text-danger carbon:text-danger-strong',
  green: 'bg-success-subtle text-success',
  gray: 'bg-surface-muted text-muted carbon:text-fg',
};

// S34-2 (design review M1) — `variant="outline"`: dolu bir durum pili DEĞİL, bir
// SINIFLANDIRMA (hazard) için. Metin `text-fg`, kenarlık NÖTR `border-strong` (hazard rengi
// DEĞİL — sarının 1.63:1 kontrastı bir kenarlık/metin rengi olarak Az Tehlikeli'yi görünmez
// yapardı, review §1 "Contrast" tablosu). Zemin TAMAMEN şeffaf (dolgu YOK) — bir `color-mix`
// dolgusu outline'ı yeniden dolu bir pile çevirirdi, tam da kaçınılması gereken şey. Rengi
// TAŞIYAN parça `icon` slot'undaki çağıran-sağlanan işaretçidir (bkz. `components/status/
// hazard.tsx`) — bu bileşen hazard'ı hiç BİLMEZ, yalnız iskeleti verir. `gap-1.5` (filled'ın
// `gap-1`'inden GENİŞ, review §1 mockup) yalnız outline'a özel — filled'ın bugünkü 45 tüketicisi
// dokunulmadan kalır.
const outlineClass = 'gap-1.5 border border-border-strong bg-transparent text-fg';

type TagProps = (
  | { variant?: 'filled'; color: TagColor }
  | { variant: 'outline'; color?: never }
) & {
  /** Sol tarafta 16px ikon (`currentColor`) — bir rengin TEK BAŞINA taşıdığı bir durumu
   * (ör. "Expiring" gri, plan §5.1) metinle birlikte AYRICA işaretlemek için, ya da outline'da
   * hazard işaretçisi (bkz. `ui/HazardTag.tsx`). Opsiyonel. */
  icon?: ReactNode;
  /** Uzun/yerelleştirilmiş metin için: `h-6` + `nowrap` yerine satır kaydırır (DE en uzunudur). */
  wrap?: boolean;
  children: ReactNode;
  className?: string;
} & Omit<HTMLAttributes<HTMLSpanElement>, 'color' | 'className' | 'children'>;

export function Tag({
  color,
  variant = 'filled',
  icon,
  wrap,
  children,
  className,
  ...rest
}: TagProps) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-full px-2 text-xs font-normal leading-4 tracking-[.32px]',
        // `TagProps`'un discriminated union'ı `variant !== 'outline'` ⇒ `color: TagColor`
        // ZORUNLU kılar (tip düzeyinde garanti) — ama destructure edilince tsc bu korelasyonu
        // KAYBEDER (ayrı bağlanmış değişkenler), bu yüzden tek `as` burada.
        variant === 'outline' ? outlineClass : colors[color as TagColor],
        wrap && 'h-auto min-h-6 max-w-full whitespace-normal break-words py-0.5',
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
          {icon}
        </span>
      ) : null}
      {children}
    </span>
  );
}
